import { supabase } from '../config/supabase';
import { EventWithRelations } from './EventService';

export interface UserEvents {
  hosted: EventWithRelations[];
  cohosted: EventWithRelations[];
  attending: EventWithRelations[];
}

export interface Connection {
  id: string;
  user_from: string;
  user_to: string;
  created_at: string;
}

export interface ConnectionWithProfile {
  id: string;
  name: string;
  avatarUrl: string;
  type: 'following' | 'followers';
  userId: string;
}

export class UserService {
  /**
   * Buscar usuarios por nombre o email (paginado simple)
   */
  static async searchUsers(query: string, limit = 25, offset = 0): Promise<{ data: any[] | null; error: any }> {
    try {
      const clean = (query || '').trim()
      if (!clean) return { data: [], error: null }
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, avatar_url')
        .or(`full_name.ilike.%${clean}%,email.ilike.%${clean}%`)
        .range(offset, offset + limit - 1)
      return { data: data || [], error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Listar usuarios (paginado)
   */
  static async listUsers(limit = 25, offset = 0): Promise<{ data: any[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email, avatar_url')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)
      return { data: data || [], error }
    } catch (error) {
      return { data: null, error }
    }
  }

  /**
   * Listar usuarios con conteo total (paginado)
   */
  static async listUsersWithCount(
    limit = 25,
    offset = 0
  ): Promise<{ data: any[]; count: number; error: any }> {
    try {
      const { data, error, count } = await supabase
        .from('profiles')
        .select('id, full_name, email, avatar_url', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      return { data: data || [], count: count || 0, error };
    } catch (error) {
      return { data: [], count: 0, error };
    }
  }

  /**
   * Buscar usuarios con conteo total (paginado)
   */
  static async searchUsersWithCount(
    query: string,
    limit = 25,
    offset = 0
  ): Promise<{ data: any[]; count: number; error: any }> {
    try {
      const clean = (query || '').trim();
      if (!clean) return { data: [], count: 0, error: null };
      const { data, error, count } = await supabase
        .from('profiles')
        .select('id, full_name, email, avatar_url', { count: 'exact' })
        .or(`full_name.ilike.%${clean}%,email.ilike.%${clean}%`)
        .range(offset, offset + limit - 1);
      return { data: data || [], count: count || 0, error };
    } catch (error) {
      return { data: [], count: 0, error };
    }
  }

  /**
   * Obtener el perfil completo (raw) de un usuario
   */
  static async getProfileRaw(userId: string): Promise<{ data: any | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      return { data, error };
    } catch (error) {
      return { data: null, error };
    }
  }
  /**
   * Obtener todos los eventos relacionados con un usuario
   */
  static async getUserEvents(userId: string): Promise<{ data: UserEvents | null; error: any }> {
    try {
      // 1) Eventos donde es host principal (created_by)
      const { data: hostedEvents, error: hostedError } = await supabase
        .from('events')
        .select('*, parent_event:events!parent_event_id(title)')
        .eq('created_by', userId)
        .eq('is_public', true)
        .order('start_date', { ascending: true });

      // 2) Eventos donde es co-host (en event_hosts pero no created_by)
      const { data: cohostData, error: cohostError } = await supabase
        .from('event_hosts')
        .select('event_id')
        .eq('user_id', userId);

      let cohostedEvents: any[] = [];
      if (cohostData && cohostData.length > 0) {
        const cohostEventIds = cohostData.map(ch => ch.event_id);
        const res = await supabase
          .from('events')
          .select('*, parent_event:events!parent_event_id(title)')
          .neq('created_by', userId) // No incluir eventos donde es el creador principal
          .in('id', cohostEventIds)
          .eq('is_public', true)
          .order('start_date', { ascending: true });
        cohostedEvents = res.data || [];
      }

      // 3) Eventos donde está asistiendo (en event_users)
      const { data: attendingData, error: attendingDataError } = await supabase
        .from('event_users')
        .select('event_id')
        .eq('user_id', userId)
        .eq('rsvp_status', 'confirmed');

      let attendingEvents: any[] = [];
      if (attendingData && attendingData.length > 0) {
        const attendingEventIds = attendingData.map(a => a.event_id);
        const res = await supabase
          .from('events')
          .select('*, parent_event:events!parent_event_id(title)')
          .in('id', attendingEventIds)
          .eq('is_public', true)
          .order('start_date', { ascending: true });
        attendingEvents = res.data || [];
      }

      if (hostedError || cohostError || attendingDataError) {
        return { data: null, error: hostedError || cohostError || attendingDataError };
      }

      // 4) Traer hosts para TODOS los eventos en un único query
      const allEventIds = Array.from(new Set([
        ...(hostedEvents?.map(e => e.id) || []),
        ...(cohostedEvents.map(e => e.id) || []),
        ...(attendingEvents.map(e => e.id) || [])
      ]));

      let hostsByEventId = new Map<string, any[]>();
      if (allEventIds.length > 0) {
        const { data: allHosts } = await supabase
          .from('event_hosts')
          .select('*')
          .in('event_id', allEventIds);

        for (const h of allHosts || []) {
          const arr = hostsByEventId.get(h.event_id) || [];
          arr.push(h);
          hostsByEventId.set(h.event_id, arr);
        }
      }

      const mapWithHosts = (event: any) => ({
        ...event,
        hosts: hostsByEventId.get(event.id) || [],
        side_events: [],
        hosts_count: (hostsByEventId.get(event.id) || []).length,
        attendees_count: 0
      }) as EventWithRelations;

      const hostedWithHosts = (hostedEvents || []).map(mapWithHosts);
      const cohostedWithHosts = cohostedEvents.map(mapWithHosts);
      const attendingWithHosts = attendingEvents.map(mapWithHosts);

      return {
        data: {
          hosted: hostedWithHosts,
          cohosted: cohostedWithHosts,
          attending: attendingWithHosts
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching user events:', error);
      return { data: null, error };
    }
  }

  /**
   * Obtener eventos por tipo específico
   */
  static async getUserEventsByType(
    userId: string, 
    type: 'hosted' | 'cohosted' | 'attending'
  ): Promise<{ data: EventWithRelations[] | null; error: any }> {
    try {
      const { data: userEvents, error } = await this.getUserEvents(userId);
      
      if (error || !userEvents) {
        return { data: null, error };
      }

      return { data: userEvents[type], error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  /**
   * Seguir a un usuario
   */
  static async follow(userFromId: string, userToId: string): Promise<{ data: Connection | null; error: any }> {
    try {
      // Verificar que no se siga a sí mismo
      if (userFromId === userToId) {
        return { data: null, error: 'No puedes seguirte a ti mismo' };
      }

      // Verificar si ya existe la conexión
      const { data: existing } = await supabase
        .from('connections')
        .select('*')
        .eq('user_from', userFromId)
        .eq('user_to', userToId)
        .single();

      if (existing) {
        return { data: null, error: 'Ya sigues a este usuario' };
      }

      const { data, error } = await supabase
        .from('connections')
        .insert({
          user_from: userFromId,
          user_to: userToId
        })
        .select()
        .single();

      return { data, error };
    } catch (error) {
      console.error('Error following user:', error);
      return { data: null, error };
    }
  }

  /**
   * Dejar de seguir a un usuario
   */
  static async unfollow(userFromId: string, userToId: string): Promise<{ success: boolean; error: any }> {
    try {
      const { error } = await supabase
        .from('connections')
        .delete()
        .eq('user_from', userFromId)
        .eq('user_to', userToId);

      return { success: !error, error };
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return { success: false, error };
    }
  }

  /**
   * Obtener conexiones de un usuario
   */
  static async getConnections(userId: string, type: 'following' | 'followers' = 'following'): Promise<{ data: string[] | null; error: any }> {
    try {
      let query = supabase.from('connections');
      let postquery;
      if (type === 'following') {
        postquery = query.select('user_to').eq('user_from', userId);
      } else {
        postquery = query.select('user_from').eq('user_to', userId);
      }

      const { data, error } = await postquery;

      if (error) {
        return { data: null, error };
      }

      const userIds = data?.map((conn: any) => type === 'following' ? conn.user_to : conn.user_from) || [];
      return { data: userIds, error: null };
    } catch (error) {
      console.error('Error getting connections:', error);
      return { data: null, error };
    }
  }

  /**
   * Obtener conexiones de un usuario con información de perfil
   */
  static async getConnectionsWithProfiles(userId: string): Promise<{ 
    data: { following: ConnectionWithProfile[]; followers: ConnectionWithProfile[] } | null; 
    error: any 
  }> {
    try {
      // Obtener usuarios que sigue
      const { data: followingData, error: followingError } = await supabase
        .from('connections')
        .select(`
          user_to,
          profiles!connections_user_to_fkey (
            id,
            full_name,
            avatar_url,
            email
          )
        `)
        .eq('user_from', userId);

      // Obtener seguidores
      const { data: followersData, error: followersError } = await supabase
        .from('connections')
        .select(`
          user_from,
          profiles!connections_user_from_fkey (
            id,
            full_name,
            avatar_url,
            email
          )
        `)
        .eq('user_to', userId);

      if (followingError || followersError) {
        return { data: null, error: followingError || followersError };
      }

      // Transformar datos de following
      const following: ConnectionWithProfile[] = (followingData || []).map((conn: any) => ({
        id: `following-${conn.user_to}`,
        name: conn.profiles?.full_name || conn.profiles?.email?.split('@')[0] || 'Unknown User',
        avatarUrl: conn.profiles?.avatar_url || '',
        type: 'following' as const,
        userId: conn.user_to
      }));

      // Transformar datos de followers
      const followers: ConnectionWithProfile[] = (followersData || []).map((conn: any) => ({
        id: `followers-${conn.user_from}`,
        name: conn.profiles?.full_name || conn.profiles?.email?.split('@')[0] || 'Unknown User',
        avatarUrl: conn.profiles?.avatar_url || '',
        type: 'followers' as const,
        userId: conn.user_from
      }));

      return {
        data: { following, followers },
        error: null
      };
    } catch (error) {
      console.error('Error getting connections with profiles:', error);
      return { data: null, error };
    }
  }
}